<template>
  <div class="registry-container">
    <h2>
      Свод по ученикам
      <span
        v-if="!isAdmin && selectedClass"
        style="color: #1976d2; font-size: 0.7em"
      >
        — {{ selectedClass.className }}
      </span>
    </h2>

    <!-- ==================== ФИЛЬТРЫ ==================== -->
    <div class="filters-bar">
      <div class="filter-item">
        <label>Класс</label>
        <select
          class="form-select"
          v-model="selectedClassID"
          :disabled="!isAdmin"
        >
          <option value="">— выберите класс —</option>
          <option v-for="c in classList" :key="c.id" :value="c.id">
            {{ c.className }}
          </option>
          <option v-if="isAdmin" value="__all__">🏫 Вся школа</option>
        </select>
      </div>

      <div class="filter-item" style="flex: 1; min-width: 220px">
        <label>Поиск по ФИО</label>
        <input
          class="form-control"
          v-model="search"
          placeholder="Фамилия, имя..."
        />
      </div>

      <button
        class="btn-print"
        @click="printRegistry"
        :disabled="!students.length"
      >
        🖨️ Печать таблицы
      </button>
    </div>

    <!-- ==================== ПРОГРЕСС ЗАГРУЗКИ ==================== -->
    <div v-if="loading" class="loading-bar">
      <span
        >Загрузка: {{ loadedClasses }} /
        {{ totalClassesToLoad }} классов...</span
      >
      <div class="progress-track">
        <div
          class="progress-fill"
          :style="{ width: progressPercent + '%' }"
        ></div>
      </div>
    </div>

    <!-- ==================== ПУСТОЕ СОСТОЯНИЕ ==================== -->
    <div v-if="!selectedClassID && !loading" class="empty-state">
      <div style="font-size: 48px">📋</div>
      <p>Выберите класс или «Вся школа», чтобы увидеть реестр учеников</p>
    </div>

    <!-- ==================== ТАБЛИЦА ==================== -->
    <div v-if="selectedClassID && !loading" class="students-table-wrapper">
      <table class="students-table">
        <thead>
          <tr>
            <th @click="sortBy('index')" class="sortable">
              №
              <span v-if="sortKey === 'index'" class="sort-arrow">
                {{ sortDir === "asc" ? "↑" : "↓" }}
              </span>
            </th>
            <th @click="sortBy('class')" class="sortable">
              Класс
              <span v-if="sortKey === 'class'" class="sort-arrow">
                {{ sortDir === "asc" ? "↑" : "↓" }}
              </span>
            </th>
            <th @click="sortBy('name')" class="sortable">
              ФИО ученика
              <span v-if="sortKey === 'name'" class="sort-arrow">
                {{ sortDir === "asc" ? "↑" : "↓" }}
              </span>
            </th>
            <th @click="sortBy('emotionalRisk')" class="sortable">
              Эмоциональный риск
              <span v-if="sortKey === 'emotionalRisk'" class="sort-arrow">
                {{ sortDir === "asc" ? "↑" : "↓" }}
              </span>
            </th>
            <th @click="sortBy('riskZone')" class="sortable">
              Риск по тепловой карте
              <span v-if="sortKey === 'riskZone'" class="sort-arrow">
                {{ sortDir === "asc" ? "↑" : "↓" }}
              </span>
            </th>
            <th @click="sortBy('violations')" class="sortable">
              Кол-во нарушений
              <span v-if="sortKey === 'violations'" class="sort-arrow">
                {{ sortDir === "asc" ? "↑" : "↓" }}
              </span>
            </th>
            <th @click="sortBy('Clubs')" class="sortable">
              Занятость
              <span v-if="sortKey === 'Clubs'" class="sort-arrow">
                {{ sortDir === "asc" ? "↑" : "↓" }}
              </span>
            </th>
            <th @click="sortBy('achievements')" class="sortable">
              Кол-во достижений
              <span v-if="sortKey === 'achievements'" class="sort-arrow">
                {{ sortDir === "asc" ? "↑" : "↓" }}
              </span>
            </th>
            <th @click="sortBy('points')" class="sortable">
              Баллы достижений
              <span v-if="sortKey === 'points'" class="sort-arrow">
                {{ sortDir === "asc" ? "↑" : "↓" }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(s, index) in filteredStudents" :key="s._id">
            <td class="student-number">{{ index + 1 }}</td>
            <td>{{ getClassName(s.classID) }}</td>
            <td class="student-name-cell">
              <strong>{{ s.FirstName }} {{ s.LastName }} </strong>
            </td>
            <td :class="getRiskClass(s)">
              {{ getEmotionalRisk(s) }}
            </td>
            <td :class="getRiskZoneClass(s)">
              {{ getRiskZoneName(s) }}
            </td>
            <td class="text-center">
              <span
                :class="s._violationsCount > 0 ? 'count-bad' : 'count-none'"
              >
                {{ s._violationsCount }}
              </span>
            </td>
            <td class="text-center">
              <span>
                {{ Array.isArray(s.Clubs) ? s.Clubs.length : 0 }}
              </span>
            </td>
            <td class="text-center">
              <span
                :class="s._achievementsCount > 0 ? 'count-good' : 'count-none'"
              >
                {{ s._achievementsCount }}
              </span>
            </td>
            <td class="text-center">
              <b class="points-good">+{{ s._achievementsPoints }}</b>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!filteredStudents.length" class="no-data">
        Учеников не найдено
      </div>
    </div>
  </div>
</template>

<script>
import TutorialDataService from "../services/TutorialDataService";

export default {
  name: "StudentsRegistry",
  props: {
    selectedClass: Object,
  },
  data() {
    return {
      classList: [],
      students: [],
      achievements: [],
      violations: [],

      selectedClassID: "",
      search: "",

      // сортировка
      sortKey: "class", // по какому полю
      sortDir: "asc", // asc | desc

      // загрузка
      loading: false,
      loadedClasses: 0,
      totalClassesToLoad: 0,
    };
  },
  computed: {
    isAdmin() {
      return this.selectedClass && this.selectedClass.classID === "admin";
    },

    progressPercent() {
      if (!this.totalClassesToLoad) return 0;
      return Math.round((this.loadedClasses / this.totalClassesToLoad) * 100);
    },

    // обогащаем учеников счётчиками
    enrichedStudents() {
      const achMap = this.groupByStudent(this.achievements);
      const violMap = this.groupByStudent(this.violations);

      return this.students.map((s) => {
        const ach = achMap[s._id] || [];
        const vio = violMap[s._id] || [];

        return {
          ...s,
          _achievementsCount: ach.length,
          _achievementsPoints: ach.reduce((sum, a) => sum + (a.points || 0), 0),
          _violationsCount: vio.length,
          _emotionalRisk: s.emotionalState ? this.calculateRiskScore(s) : -1,
          _riskZone: this.getStudentZone(s),
        };
      });
    },

    filteredStudents() {
      let list = [...this.enrichedStudents];

      // поиск
      if (this.search.trim()) {
        const q = this.search.toLowerCase();
        list = list.filter(
          (s) =>
            (s.FirstName || "").toLowerCase().includes(q) ||
            (s.LastName || "").toLowerCase().includes(q) ||
            (s.Surname || "").toLowerCase().includes(q),
        );
      }

      // сортировка
      const dir = this.sortDir === "asc" ? 1 : -1;
      list.sort((a, b) => {
        let va, vb;
        switch (this.sortKey) {
          case "class":
            va = this.getClassName(a.classID);
            vb = this.getClassName(b.classID);
            return va.localeCompare(vb, "ru") * dir;

          case "name":
            va = `${a.LastName} ${a.FirstName}`;
            vb = `${b.LastName} ${b.FirstName}`;
            return va.localeCompare(vb, "ru") * dir;

          case "emotionalRisk":
            va = a._emotionalRisk;
            vb = b._emotionalRisk;
            break;

          case "riskZone": {
            // сортируем по «весу» зоны
            const zoneWeight = { red: 3, yellow: 2, green: 1, none: 0 };
            va = zoneWeight[a._riskZone] ?? 0;
            vb = zoneWeight[b._riskZone] ?? 0;
            break;
          }

          case "violations":
            va = a._violationsCount;
            vb = b._violationsCount;
            break;

          case "Clubs":
            va = Array.isArray(a.Clubs) ? a.Clubs.length : 0;
            vb = Array.isArray(b.Clubs) ? b.Clubs.length : 0;
            break;

          case "achievements":
            va = a._achievementsCount;
            vb = b._achievementsCount;
            break;

          case "points":
            va = a._achievementsPoints;
            vb = b._achievementsPoints;
            break;

          case "index":
          default:
            return 0;
        }

        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });

      return list;
    },
  },
  watch: {
    async selectedClassID(newVal) {
      // не-админам запрещаем «Вся школа»
      if (!this.isAdmin && newVal === "__all__") {
        this.selectedClassID = this.selectedClass.classID;
        return;
      }

      if (!newVal) {
        this.students = [];
        return;
      }
      if (newVal === "__all__") {
        await this.loadAllSchool();
      } else {
        await this.loadClassStudents(newVal);
      }
    },
  },
  methods: {
    // ==================== ЗАГРУЗКА ====================
    async loadInitial() {
      try {
        const [classesRes, achRes, violRes] = await Promise.all([
          TutorialDataService.getAllCLass(),
          TutorialDataService.getAllAchievements(),
          TutorialDataService.getAllViolations(),
        ]);

        this.classList = classesRes.data.map((c) => ({
          id: c._id,
          className: c.className,
        }));
        this.achievements = achRes.data || [];
        this.violations = violRes.data || [];
      } catch (e) {
        console.error("Ошибка загрузки:", e);
      }
    },

    async loadClassStudents(classID) {
      this.loading = true;
      this.loadedClasses = 0;
      this.totalClassesToLoad = 1;
      this.students = [];

      const c = this.classList.find((x) => x.id === classID);
      if (!c) {
        this.loading = false;
        return;
      }

      try {
        const r = await TutorialDataService.findStudentByClassID({
          classID: c.id,
          className: c.className,
        });
        const list = Object.values(r.data || {}).filter((s) => s && s._id);
        this.students = list;
        this.loadedClasses = 1;
      } catch (e) {
        console.error(e);
      }

      this.loading = false;
    },

    async loadAllSchool() {
      if (
        !confirm(
          "Загрузить всех учеников школы? Это может занять время — данные будут появляться по мере загрузки.",
        )
      ) {
        this.selectedClassID = "";
        return;
      }

      this.loading = true;
      this.loadedClasses = 0;
      this.totalClassesToLoad = this.classList.length;
      this.students = [];

      const seen = new Set();

      for (const c of this.classList) {
        try {
          const r = await TutorialDataService.findStudentByClassID({
            classID: c.id,
            className: c.className,
          });
          const list = Object.values(r.data || {}).filter(
            (s) => s && s._id && !seen.has(s._id),
          );
          list.forEach((s) => seen.add(s._id));
          this.students = [...this.students, ...list];
          this.loadedClasses++;
        } catch (e) {
          console.warn("Не удалось загрузить", c.className, e);
          this.loadedClasses++;
        }
      }

      this.loading = false;
    },

    // ==================== СОРТИРОВКА ====================
    sortBy(key) {
      if (this.sortKey === key) {
        // клик по тому же полю — переключаем направление
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        // разумный дефолт: числовые — по убыванию, текстовые — по возрастанию
        this.sortDir = [
          "points",
          "achievements",
          "violations",
          "emotionalRisk",
          "riskZone",
          "Clubs",
        ].includes(key)
          ? "desc"
          : "asc";
      }
    },

    // ==================== ГРУППИРОВКА ====================
    groupByStudent(items) {
      const m = {};
      items.forEach((it) => {
        (it.studentID || []).forEach((s) => {
          const id = s._id || s;
          if (!id) return;
          if (!m[id]) m[id] = [];
          m[id].push(it);
        });
      });
      return m;
    },

    // ==================== РИСКИ (как в AIC.vue) ====================
    getEmotionalRisk(student) {
      return student.emotionalState
        ? this.calculateRiskScore(student) + "%"
        : "нет данных";
    },

    calculateRiskScore(student) {
      if (!student.emotionalState) return 0;

      const state = student.emotionalState;
      let score = 0;
      let total = 0;

      const weights = {
        mood: 1,
        social: 1,
        behavior: 2,
        thoughts: 3,
        circumstances: 1.5,
      };

      Object.values(state.mood || {}).forEach((val) => {
        if (val) score += weights.mood;
        total += weights.mood;
      });
      Object.values(state.social || {}).forEach((val) => {
        if (val) score += weights.social;
        total += weights.social;
      });
      Object.values(state.behavior || {}).forEach((val) => {
        if (val) score += weights.behavior;
        total += weights.behavior;
      });
      Object.values(state.thoughts || {}).forEach((val) => {
        if (val) score += weights.thoughts;
        total += weights.thoughts;
      });
      Object.values(state.circumstances || {}).forEach((val) => {
        if (val) score += weights.circumstances;
        total += weights.circumstances;
      });

      return total > 0 ? Math.round((score / total) * 100) : 0;
    },

    getRiskClass(student) {
      const riskScore = student.emotionalState
        ? this.calculateRiskScore(student)
        : null;
      if (!riskScore) return "";
      if (riskScore >= 70) return "risk-high";
      if (riskScore >= 40) return "risk-medium";
      return "risk-low";
    },

    getStudentZone(student) {
      if (!student.riskZones) return "none";

      if (
        student.riskZones.red &&
        Object.values(student.riskZones.red).some((v) => v)
      )
        return "red";
      if (
        student.riskZones.yellow &&
        Object.values(student.riskZones.yellow).some((v) => v)
      )
        return "yellow";
      if (
        student.riskZones.green &&
        Object.values(student.riskZones.green).some((v) => v)
      )
        return "green";
      return "none";
    },

    getRiskZoneName(student) {
      const zone = this.getStudentZone(student);
      const map = {
        red: "Высокий",
        yellow: "Средний",
        green: "Низкий",
        none: "Не определен",
      };
      return map[zone] || "Не определен";
    },

    getRiskZoneClass(student) {
      const zone = this.getRiskZoneName(student);
      if (zone === "Высокий") return "risk-high";
      if (zone === "Средний") return "risk-medium";
      if (zone === "Низкий") return "risk-low";
      return "";
    },

    // ==================== УТИЛИТЫ ====================
    getClassName(id) {
      const c = this.classList.find((x) => x.id === id);
      return c ? c.className : id || "—";
    },

    // ==================== ПЕЧАТЬ ====================
    printRegistry() {
      const win = window.open("", "_blank");

      const rows = this.filteredStudents
        .map(
          (s, i) => `
        <tr>
          <td>${i + 1}</td>
          <td>${this.getClassName(s.classID)}</td>
          <td><strong>${s.LastName} ${s.FirstName}</strong></td>
          <td class="${this.getRiskClass(s)}">${this.getEmotionalRisk(s)}</td>
          <td class="${this.getRiskZoneClass(s)}">${this.getRiskZoneName(
            s,
          )}</td>
          <td class="text-center">${s._violationsCount}</td>
          <td class="text-center">${
            Array.isArray(s.Clubs) ? s.Clubs.length : 0
          }</td>
          <td class="text-center">${s._achievementsCount}</td>
          <td class="text-center"><strong>+${
            s._achievementsPoints
          }</strong></td>
        </tr>
      `,
        )
        .join("");

      const classLabel =
        this.selectedClassID === "__all__"
          ? "Вся школа"
          : this.getClassName(this.selectedClassID);

      win.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Реестр учеников — ${classLabel}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Segoe UI', Tahoma, sans-serif;
              padding: 20px;
              font-size: 12px;
            }
            .report-header {
              text-align: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 2px solid #333;
            }
            .report-header h1 { font-size: 18px; margin-bottom: 5px; }
            .report-header p { color: #666; font-size: 11px; }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px 6px;
              text-align: left;
              font-size: 11px;
            }
            th {
              background: #f0f0f0;
              font-weight: bold;
            }
            .text-center { text-align: center; }
            .risk-high { color: #c62828; font-weight: bold; }
            .risk-medium { color: #f57c00; font-weight: bold; }
            .risk-low { color: #2e7d32; font-weight: bold; }
            .footer {
              margin-top: 20px;
              text-align: center;
              font-size: 10px;
              color: #666;
              border-top: 1px solid #ddd;
              padding-top: 10px;
            }
          </style>
        </head>
        <body>
          <div class="report-header">
            <h1>Реестр учеников — ${classLabel}</h1>
            <p>Дата формирования: ${new Date().toLocaleString("ru-RU")}</p>
            <p>Всего учеников: ${this.filteredStudents.length}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>№</th>
                <th>Класс</th>
                <th>ФИО ученика</th>
                <th>Эмоциональный риск</th>
                <th>Риск по тепловой карте</th>
                <th>Кол-во нарушений</th>
                <th>Занятость</th>
                <th>Кол-во достижений</th>
                <th>Баллы достижений</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <div class="footer">
            <p>Отчёт сформирован автоматически</p>
          </div>
        </body>
        </html>
      `);

      win.document.close();
      win.onload = () => win.print();
    },
  },

  async mounted() {
    // если пользователь не админ — жёстко фиксируем его класс
    if (!this.isAdmin && this.selectedClass) {
      this.selectedClassID = this.selectedClass.classID;
    }

    await this.loadInitial();

    // для класса — сразу подгружаем его учеников
    if (!this.isAdmin && this.selectedClassID) {
      await this.loadClassStudents(this.selectedClassID);
    }
  },
};
</script>

<style scoped>
.registry-container {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

h2 {
  margin-bottom: 20px;
}

/* ==================== ФИЛЬТРЫ ==================== */
.filters-bar {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}
.filter-item {
  display: flex;
  flex-direction: column;
  min-width: 180px;
}
.filter-item label {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 3px;
}
.btn-print {
  background: #28a745;
  color: white;
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}
.btn-print:hover:not(:disabled) {
  background: #218838;
}
.btn-print:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* ==================== ПРОГРЕСС ==================== */
.loading-bar {
  padding: 12px 15px;
  background: #e3f2fd;
  border-left: 4px solid #1976d2;
  border-radius: 4px;
  margin-bottom: 20px;
  font-weight: 500;
  color: #0d47a1;
}
.progress-track {
  height: 6px;
  background: #bbdefb;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 8px;
}
.progress-fill {
  height: 100%;
  background: #1976d2;
  transition: width 0.2s ease;
}

/* ==================== ПУСТОЕ СОСТОЯНИЕ ==================== */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #f8f9fa;
  border-radius: 8px;
  color: #666;
}
.empty-state p {
  margin-top: 10px;
  font-size: 15px;
}

/* ==================== ТАБЛИЦА ==================== */
.students-table-wrapper {
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
}
.students-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.students-table th {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  padding: 12px 10px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
  user-select: none;
}
.students-table th.sortable {
  cursor: pointer;
  transition: background 0.15s;
}
.students-table th.sortable:hover {
  background: #e9ecef;
}
.sort-arrow {
  color: #1976d2;
  font-weight: bold;
  margin-left: 4px;
}
.students-table td {
  border: 1px solid #dee2e6;
  padding: 10px;
  vertical-align: middle;
}
.students-table tr:hover {
  background: #f5f9ff;
}
.student-number {
  color: #6c757d;
  font-weight: 500;
  text-align: center;
  width: 45px;
}
.student-name-cell {
  min-width: 200px;
}
.text-center {
  text-align: center;
}

/* ==================== ЦВЕТА РИСКОВ ==================== */
.risk-high {
  color: #c62828;
  font-weight: bold;
}
.risk-medium {
  color: #f57c00;
  font-weight: bold;
}
.risk-low {
  color: #2e7d32;
  font-weight: bold;
}

/* ==================== СЧЁТЧИКИ ==================== */
.count-bad {
  display: inline-block;
  padding: 3px 10px;
  background: #ffcdd2;
  color: #c62828;
  font-weight: bold;
  border-radius: 12px;
  min-width: 30px;
}
.count-good {
  display: inline-block;
  padding: 3px 10px;
  background: #c8e6c9;
  color: #2e7d32;
  font-weight: bold;
  border-radius: 12px;
  min-width: 30px;
}
.count-none {
  display: inline-block;
  padding: 3px 10px;
  color: #999;
}
.points-good {
  color: #2e7d32;
  font-size: 14px;
}

.no-data {
  text-align: center;
  color: #999;
  padding: 40px;
}

select:disabled {
  background: #e9ecef;
  color: #495057;
  cursor: not-allowed;
  opacity: 1;
}
</style>
